import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { statementsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { Statement, StatementItem } from "../../modules/statementTypes";

const StatementDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();

  const [statement, setStatement] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showIssueConfirm, setShowIssueConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 0,
    date: new Date().toISOString().split("T")[0],
    method: "Bank Transfer",
  });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    const fetchStatementDetails = async () => {
      if (!id || !currentBuilding?.id) return;

      try {
        setLoading(true);
        const data = await statementsApi.get(id);
        setStatement(data);
        if (data.status === "paid") {
          setPaymentDetails({
            amount: data.paidAmount || data.totalAmount,
            date: data.paidDate || new Date().toISOString().split("T")[0],
            method: data.paymentMethod || "Bank Transfer",
          });
        } else {
          setPaymentDetails({
            amount: data.totalAmount,
            date: new Date().toISOString().split("T")[0],
            method: "Bank Transfer",
          });
        }
      } catch (err) {
        console.error("Error fetching statement details:", err);
        setError("Failed to load statement details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchStatementDetails();
  }, [id, currentBuilding?.id]);

  const handleDeleteStatement = async () => {
    if (!id) return;

    try {
      setLoading(true);
      await statementsApi.delete(id);
      navigate("/dashboard/statements");
    } catch (err) {
      console.error("Error deleting statement:", err);
      setError("Failed to delete statement. Please try again.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleIssueStatement = async () => {
    if (!statement || !id) return;

    try {
      setLoading(true);
      await statementsApi.update(id, { status: "issued" });
      setStatement({
        ...statement,
        status: "issued",
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error issuing statement:", err);
      setError("Failed to issue statement. Please try again.");
    } finally {
      setLoading(false);
      setShowIssueConfirm(false);
    }
  };

  const handleSendStatement = async () => {
    if (!statement || !id) return;

    try {
      setIsSendingEmail(true);
      await statementsApi.generatePdf(id);
      alert("Statement sent successfully to " + statement.residentEmail);
    } catch (err) {
      console.error("Error sending statement:", err);
      setError("Failed to send statement. Please try again.");
    } finally {
      setIsSendingEmail(false);
      setShowSendConfirm(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!statement || !id) return;

    try {
      setLoading(true);
      await statementsApi.update(id, {
        status: "paid",
        paidAmount: paymentDetails.amount,
        paidDate: paymentDetails.date,
        paymentMethod: paymentDetails.method,
      });
      setStatement({
        ...statement,
        status: "paid",
        paidAmount: paymentDetails.amount,
        paidDate: paymentDetails.date,
        paymentMethod: paymentDetails.method,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Error marking statement as paid:", err);
      setError("Failed to update payment status. Please try again.");
    } finally {
      setLoading(false);
      setShowMarkPaidDialog(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!statement || !id) return;

    try {
      setIsGeneratingPdf(true);
      const result = await statementsApi.generatePdf(id);
      const url = result?.url || statement.documentUrl || "";
      if (url) window.open(url, "_blank");
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePaymentDetailsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setPaymentDetails((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) || 0 : value,
    }));
  };

  if (loading && !statement) {
    return <Spinner message="Loading statement details..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!statement) {
    return <ErrorAlert message="Statement not found" />;
  }

  const isEditable = statement.status === "draft";
  const isPaid = statement.status === "paid";
  const canMarkAsPaid =
    statement.status === "issued" || statement.status === "overdue";
  const canSendEmail =
    statement.status !== "draft" &&
    statement.documentUrl &&
    statement.residentEmail;

  return (
    <div className="">
      <PageHeader
        title={`Statement for ${statement.unitName}`}
        description={`${statement.period} - ${formatDate(statement.issueDate)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {isEditable && (
              <>
                <button
                  onClick={() => setShowIssueConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  Issue Statement
                </button>
                <button
                  onClick={() =>
                    navigate(`/dashboard/statements/edit/${statement.id}`)
                  }
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Edit
                </button>
              </>
            )}

            {canMarkAsPaid && (
              <button
                onClick={() => setShowMarkPaidDialog(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none"
              >
                Mark as Paid
              </button>
            )}

            {statement.documentUrl && (
              <button
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50"
              >
                {isGeneratingPdf ? "Generating..." : "View PDF"}
              </button>
            )}

            {canSendEmail && (
              <button
                onClick={() => setShowSendConfirm(true)}
                disabled={isSendingEmail}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50"
              >
                {isSendingEmail ? "Sending..." : "Send by Email"}
              </button>
            )}

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
            >
              Delete
            </button>
          </div>
        }
      />

      {/* Statement Status Banner */}
      <div
        className={`mt-6 px-4 py-3 rounded-md ${
          statement.status === "paid"
            ? "bg-green-50 border border-green-200"
            : statement.status === "overdue"
            ? "bg-red-50 border border-red-200"
            : statement.status === "issued"
            ? "bg-primary-50 border border-blue-200"
            : "bg-yellow-50 border border-yellow-200"
        }`}
      >
        <div className="flex">
          <div className="flex-shrink-0">
            {statement.status === "paid" ? (
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : statement.status === "overdue" ? (
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : statement.status === "issued" ? (
              <svg
                className="h-5 w-5 text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                <path
                  fillRule="evenodd"
                  d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="ml-3">
            <h3
              className={`text-sm font-medium ${
                statement.status === "paid"
                  ? "text-green-800"
                  : statement.status === "overdue"
                  ? "text-red-800"
                  : statement.status === "issued"
                  ? "text-primary-800"
                  : "text-yellow-800"
              }`}
            >
              {statement.status === "paid"
                ? "Payment Received"
                : statement.status === "overdue"
                ? "Payment Overdue"
                : statement.status === "issued"
                ? "Statement Issued"
                : "Draft Statement"}
            </h3>
            <div className="mt-2 text-sm">
              <p
                className={
                  statement.status === "paid"
                    ? "text-green-700"
                    : statement.status === "overdue"
                    ? "text-red-700"
                    : statement.status === "issued"
                    ? "text-primary-700"
                    : "text-yellow-700"
                }
              >
                {statement.status === "paid"
                  ? `Payment of ${formatCurrency(
                      statement.paidAmount || 0,
                    )} received on ${formatDate(
                      statement.paidDate || "",
                    )} via ${statement.paymentMethod}`
                  : statement.status === "overdue"
                  ? `Payment of ${formatCurrency(
                      statement.totalAmount,
                    )} was due on ${formatDate(statement.dueDate)}`
                  : statement.status === "issued"
                  ? `Payment of ${formatCurrency(
                      statement.totalAmount,
                    )} is due by ${formatDate(statement.dueDate)}`
                  : "This statement has not been issued yet"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statement Details */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Statement Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Details and status for this service charge statement.
            </p>
          </div>
          <div>
            <span
              className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium
              ${
                statement.status === "paid"
                  ? "bg-green-100 text-green-800"
                  : statement.status === "overdue"
                  ? "bg-red-100 text-red-800"
                  : statement.status === "issued"
                  ? "bg-primary-100 text-primary-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {statement.status
                ? statement.status.charAt(0).toUpperCase() +
                  statement.status.slice(1)
                : "Pending"}
            </span>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Unit</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {statement.unitName}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Resident</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {statement.residentName}
                {statement.residentEmail && (
                  <span className="ml-2 text-gray-500">
                    ({statement.residentEmail})
                  </span>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Period</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {statement.period}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(statement.issueDate)}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Due Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(statement.dueDate)}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Total Amount
              </dt>
              <dd className="mt-1 text-sm font-bold text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency((statement.totalAmount ?? 0) / 100)}
              </dd>
            </div>
            {isPaid && (
              <>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Payment Amount
                  </dt>
                  <dd className="mt-1 text-sm text-green-600 font-medium sm:mt-0 sm:col-span-2">
                    {formatCurrency((statement.paidAmount ?? 0) / 100)}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Payment Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(statement.paidDate || "")}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Payment Method
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {statement.paymentMethod}
                  </dd>
                </div>
              </>
            )}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Notes</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {statement.notes || "No notes provided"}
              </dd>
            </div>
            {statement.documentUrl && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Statement Document
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <a
                    href={statement.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-500 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 mr-1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View Statement PDF
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Statement Items */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Statement Items
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Breakdown of charges included in this statement.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Category
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(statement.items ?? []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-6 py-4 text-sm text-gray-500 text-center"
                    >
                      No statement items.
                    </td>
                  </tr>
                ) : (
                  (statement.items ?? []).map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <th
                    scope="row"
                    colSpan={2}
                    className="px-6 py-3 text-left text-sm font-medium text-gray-900"
                  >
                    Total
                  </th>
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {formatCurrency(statement.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Cross-reference panel (crossref-bug Slice 2c). When the
          statement was generated by aggregating FinancialRecords, the
          `sourceFinancialRecordIds` array lists every FR that
          contributed to `totalAmount`. Render each as a clickable link
          to the FR detail page so the treasurer can verify the
          statement traces back to real transactions and jump into any
          one of them. Slice 2b keeps `paidAmount` in sync when those
          FRs settle, so the "outstanding" figure here is always
          accurate. Hidden when the statement isn't sourced from FRs
          (e.g. manual statements or draft rows). */}
      {statement.sourceFinancialRecordIds &&
        statement.sourceFinancialRecordIds.length > 0 && (
          <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Source Financial Records
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Every transaction that fed into this statement's total. Click
                any id to open the record.
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
              <ul className="divide-y divide-gray-100">
                {statement.sourceFinancialRecordIds.map((frId) => (
                  <li key={frId} className="py-2 flex items-center gap-3">
                    <Link
                      to={`/dashboard/financial-records/${frId}?buildingId=${statement.buildingId}`}
                      className="text-sm text-primary-600 hover:underline font-mono"
                    >
                      {frId}
                    </Link>
                    <span className="text-xs text-gray-400">→</span>
                    <span className="text-xs text-gray-500">view record</span>
                  </li>
                ))}
              </ul>
              {statement.sourceCategories &&
                statement.sourceCategories.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1">
                      Contributing categories
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {statement.sourceCategories.map((cat) => (
                        <span
                          key={cat}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 font-mono"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteStatement}
        title="Delete Statement"
        message={`Are you sure you want to delete the statement for ${statement.unitName} (${statement.period})? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Issue Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showIssueConfirm}
        onClose={() => setShowIssueConfirm(false)}
        onConfirm={handleIssueStatement}
        title="Issue Statement"
        message={`Are you sure you want to issue this statement to ${statement.unitName}? This will mark it as issued and make it visible to the resident.`}
        confirmText="Issue Statement"
        confirmButtonClass="bg-primary-600 hover:bg-primary-700"
      />

      {/* Send Email Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        onConfirm={handleSendStatement}
        title="Send Statement by Email"
        message={`Send this statement to ${statement.residentName} at ${statement.residentEmail}?`}
        confirmText="Send Email"
        confirmButtonClass="bg-primary-600 hover:bg-primary-700"
      />

      {/* Mark as Paid Dialog */}
      <div
        className={`fixed z-10 inset-0 overflow-y-auto ${
          showMarkPaidDialog ? "block" : "hidden"
        }`}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3
                  className="text-lg leading-6 font-medium text-gray-900"
                  id="modal-title"
                >
                  Mark Statement as Paid
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Enter the payment details to record for this statement.
                  </p>
                </div>
                <div className="mt-4 space-y-4">
                  <div>
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Payment Amount
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">£</span>
                      </div>
                      <input
                        type="number"
                        name="amount"
                        id="amount"
                        value={paymentDetails.amount}
                        onChange={handlePaymentDetailsChange}
                        className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="date"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Payment Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      id="date"
                      value={paymentDetails.date}
                      onChange={handlePaymentDetailsChange}
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="method"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Payment Method
                    </label>
                    <select
                      id="method"
                      name="method"
                      value={paymentDetails.method}
                      onChange={handlePaymentDetailsChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Direct Debit">Direct Debit</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleMarkAsPaid}
              >
                Mark as Paid
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => setShowMarkPaidDialog(false)}
                ref={(el) => el && el.focus()}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatementDetails;
